<template>
  <div
    class="autocomplete-wrapper"
    v-click-outside="closeSuggestions"
  >
    <base-input
      ref="inputRef"
      :model-value="modelValue"
      :placeholder="placeholder"
      :invalid="invalid"
      :clearable="true"
      autocomplete="off"
      @update:model-value="onInput"
      @keydown="onKeydown"
      @paste="onPaste"
      @clear="onClear"
    />

    <div
      v-if="
        showSuggestions &&
        (suggestions.length || isLoading || hasError)
      "
      class="suggestions-container"
    >
      <div v-if="isLoading" class="suggestions-loading">
        Загрузка...
      </div>

      <div v-else-if="hasError" class="suggestions-error">
        Ошибка загрузки. Попробуйте еще раз.
      </div>

      <div
        v-else-if="!suggestions.length && searchAttempted"
        class="suggestions-empty"
      >
        Организации не найдены
      </div>

      <ul v-else class="suggestions-list">
        <li
          v-for="(suggestion, index) in suggestions"
          :key="index"
          class="suggestion-item"
          :class="{
            'suggestion-item--active': index === activeIndex,
          }"
          @click="selectSuggestion(suggestion)"
          @mouseenter="activeIndex = index"
        >
          <span class="suggestion-name">
            {{ suggestion.data.name.short_with_opf }}
          </span>
          <span class="suggestion-meta">
            ИНН: {{ suggestion.data.inn || "—" }}
            <template v-if="suggestion.data.address?.value">
              · {{ suggestion.data.address.value }}
            </template>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import suggestionsApi from "@/services/api/suggestionsApi";

export default {
  name: "BasePartyAutocomplete",
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "Введите название или ИНН организации",
    },
    invalid: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "select"],
  data() {
    return {
      suggestions: [],
      lastSuccessfulSuggestions: [],
      showSuggestions: false,
      debounceTimeout: null,
      isLoading: false,
      hasError: false,
      searchAttempted: false,
      activeIndex: -1,
      selectedValue: null,
      isManuallySelected: false,
    };
  },
  mounted() {
    const inputElement =
      this.$refs.inputRef?.$el?.querySelector("input");
    if (inputElement) {
      inputElement.addEventListener("focus", this.onFocus);
      inputElement.addEventListener("click", this.onInputClick);
    }
  },
  beforeUnmount() {
    const inputElement =
      this.$refs.inputRef?.$el?.querySelector("input");
    if (inputElement) {
      inputElement.removeEventListener("focus", this.onFocus);
      inputElement.removeEventListener("click", this.onInputClick);
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  },
  methods: {
    onInput(value) {
      this.$emit("update:modelValue", value);

      if (this.selectedValue !== value) {
        this.isManuallySelected = false;
        this.selectedValue = null;
      }

      this.hasError = false;
      this.searchAttempted = false;

      if (!value || value.trim().length === 0) {
        this.suggestions = [...this.lastSuccessfulSuggestions];
        this.showSuggestions = this.suggestions.length > 0;
        return;
      }

      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      this.debounceTimeout = setTimeout(() => {
        this.fetchSuggestions(value);
      }, 1000);
    },

    onFocus() {
      if (this.lastSuccessfulSuggestions.length > 0) {
        this.suggestions = [...this.lastSuccessfulSuggestions];
        this.showSuggestions = true;
        this.activeIndex = -1;
        return;
      }

      if (
        this.modelValue &&
        this.modelValue.trim().length > 0 &&
        !this.isManuallySelected
      ) {
        this.fetchSuggestions(this.modelValue);
      }
    },

    onInputClick() {
      if (
        this.lastSuccessfulSuggestions.length > 0 &&
        !this.showSuggestions
      ) {
        this.suggestions = [...this.lastSuccessfulSuggestions];
        this.showSuggestions = true;
        this.activeIndex = -1;
      }
    },

    onPaste(event) {
      this.isManuallySelected = false;
      this.selectedValue = null;

      this.$nextTick(() => {
        const pastedText =
          event.clipboardData?.getData("text") || this.modelValue;
        if (pastedText && pastedText.trim().length > 0) {
          if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout);
          }
          this.debounceTimeout = setTimeout(() => {
            this.fetchSuggestions(pastedText.trim());
          }, 100);
        }
      });
    },

    onClear() {
      this.suggestions = [];
      this.lastSuccessfulSuggestions = [];
      this.showSuggestions = false;
      this.selectedValue = null;
      this.isManuallySelected = false;
      this.activeIndex = -1;
      this.searchAttempted = false;
      this.$emit("select", null);
    },

    onKeydown(event) {
      if (!this.showSuggestions || this.suggestions.length === 0)
        return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          this.activeIndex = Math.min(
            this.activeIndex + 1,
            this.suggestions.length - 1
          );
          this.scrollToActive();
          break;

        case "ArrowUp":
          event.preventDefault();
          this.activeIndex = Math.max(this.activeIndex - 1, -1);
          this.scrollToActive();
          break;

        case "Enter":
          event.preventDefault();
          if (
            this.activeIndex >= 0 &&
            this.suggestions[this.activeIndex]
          ) {
            this.selectSuggestion(this.suggestions[this.activeIndex]);
          }
          break;

        case "Escape":
          event.preventDefault();
          this.closeSuggestions();
          break;
      }
    },

    scrollToActive() {
      this.$nextTick(() => {
        const container = this.$el.querySelector(".suggestions-list");
        const activeItem = this.$el.querySelector(
          ".suggestion-item--active"
        );

        if (container && activeItem) {
          const containerRect = container.getBoundingClientRect();
          const itemRect = activeItem.getBoundingClientRect();

          if (
            itemRect.bottom > containerRect.bottom ||
            itemRect.top < containerRect.top
          ) {
            activeItem.scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
        }
      });
    },

    async fetchSuggestions(query) {
      if (!query || query.trim().length === 0) return;

      this.isLoading = true;
      this.hasError = false;
      this.searchAttempted = true;
      this.showSuggestions = true;

      try {
        const response = await suggestionsApi.party(query.trim());
        const fetchedSuggestions =
          response.data.body?.suggestions ||
          response.data?.suggestions ||
          [];

        this.suggestions = fetchedSuggestions;

        if (fetchedSuggestions.length > 0) {
          this.lastSuccessfulSuggestions = [...fetchedSuggestions];
        }

        this.activeIndex = -1;
      } catch (error) {
        console.error("Ошибка автокомплита организации:", error);
        this.hasError = true;

        if (this.lastSuccessfulSuggestions.length > 0) {
          this.suggestions = [...this.lastSuccessfulSuggestions];
        } else {
          this.suggestions = [];
        }
      } finally {
        this.isLoading = false;
      }
    },

    selectSuggestion(suggestion) {
      if (!suggestion) return;

      this.selectedValue = suggestion.value;
      this.isManuallySelected = true;
      this.$emit("update:modelValue", suggestion.value);
      this.$emit("select", suggestion);

      this.showSuggestions = false;
      this.activeIndex = -1;
    },

    closeSuggestions() {
      this.showSuggestions = false;
      this.activeIndex = -1;
    },

    forceUpdateValue(value) {
      this.selectedValue = value;
      this.isManuallySelected = true;
      this.$emit("update:modelValue", value);
    },
  },
  directives: {
    clickOutside: {
      mounted(el, binding) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unmounted(el) {
        document.body.removeEventListener(
          "click",
          el.clickOutsideEvent
        );
      },
    },
  },
};
</script>

<style scoped>
.autocomplete-wrapper {
  position: relative;
  width: 100%;
}

.suggestions-container {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #dfe3e8;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-height: 300px;
  overflow-y: auto;
}

.suggestions-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.suggestion-item {
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f4f6f8;
  display: flex;
  flex-direction: column;
  gap: 3px;
  transition: background-color 0.2s;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover,
.suggestion-item--active {
  background-color: #f4f6f8;
}

.suggestion-name {
  font-size: 14px;
  color: #212b36;
  font-weight: 500;
}

.suggestion-meta {
  font-size: 12px;
  color: #637381;
}

.suggestions-loading,
.suggestions-error,
.suggestions-empty {
  padding: 12px;
  text-align: center;
  font-size: 14px;
  color: #637381;
}

.suggestions-error {
  color: var(--color-danger);
}

.suggestions-empty {
  color: #919eab;
}
</style>
